import { computed, onBeforeMount, onBeforeUnmount, useTemplateRef } from 'vue'

import type { ComputedRef } from 'vue'
import type { ModalLayout } from '@brighterly/lib-lumee-ui'
import type { AppModalProps } from './interfaces'

import { useCommonStore } from '@shared/model'
import { qaConfig } from '@shared/config'

import { useKeyboardInset } from './keyboard-inset.hook'

interface AppModalOptions {
  props: AppModalProps
  onClose: () => void
  onBack: () => void
}

interface AppModalReturn {
  modalDataQa: string
  modalStyle: ComputedRef<{ '--keyboard-inset': string }>
  isActionsRow: ComputedRef<boolean>
  actionsClass: ComputedRef<string>
  closeDataQaMap: { close: string }
  handleClose: () => void
  handleBack: () => void
  handleScrimClick: () => Promise<void>
}

export const useAppModal = ({ props, onClose, onBack }: AppModalOptions): AppModalReturn => {
  const commonStore = useCommonStore()

  const layout = useTemplateRef<InstanceType<typeof ModalLayout>>('layout')

  const { keyboardInset } = useKeyboardInset()

  const modalStyle = computed<{ '--keyboard-inset': string }>(() => ({ '--keyboard-inset': `${keyboardInset.value}px` }))

  const isActionsRow = computed<boolean>(() => props.actions !== 'stack')

  const actionsClass = computed<string>(() => `app-modal__actions--${props.actions}`)

  const handleClose = (): void => {
    onClose()
  }

  const handleBack = (): void => {
    onBack()
  }

  const handleScrimClick = async (): Promise<void> => {
    if (!props.closeOnScrim) return
    if (props.loading) return

    await layout.value?.dismiss()
    onClose()
  }

  onBeforeMount(() => {
    commonStore.setModalShowed(true)
  })

  onBeforeUnmount(() => {
    commonStore.setModalShowed(false)
  })

  return {
    modalDataQa: qaConfig.attributes.modal,
    modalStyle,
    isActionsRow,
    actionsClass,
    closeDataQaMap: { close: qaConfig.attributes.buttonClose },
    handleClose,
    handleBack,
    handleScrimClick,
  }
}
